/* eslint-disable max-lines-per-function */
import { Form, Formik, FormikConfig } from "formik";
import React from "react";
import { Grid, Typography } from "@mui/material";
import * as y from "yup";
import { HistoryBackButton } from "components/buttons";
import { Checkbox, SubmitButton } from "components/haven-design/form";
import { MaterialFormikSelectAutocomplete } from "components/haven-design/form/select";
import { InputWithErrorLabel as Input } from "components/haven-design/form/inputs";
import { extendedStatesList } from "components/haven-design/state-picker";
import { nullableString } from "utils/yup-helpers";
import { AdminFormLayout } from "../shared/admin-form";

export type OrgForm = {
  name?: string;
  type?: string;
  email?: string;
  bccEmail?: string;
  phone?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
  invoicedCustomer?: {
    active: boolean;
    invoicedId: string;
  };
}

type Props = {
  initialData: OrgForm;
  formType: "update" | "new";
  handleSubmit: FormikConfig<OrgForm>["onSubmit"]
  sending: boolean;
}

export const formSchema = y.object().shape({
  __typename: y.string().strip(true),

  id: y.string().strip(true),

  name: y
    .string()
    .trim()
    .required(),

  // this value is passed in on initial data and not changed by the user.
  // Since we want to ensure it gets into the database so I am putting this here.
  type: y
    .string()
    .trim()
    .required(),

  email: nullableString().required(),

  bccEmail: nullableString(),

  phone: nullableString().required(),

  address1: nullableString().required(),

  address2: nullableString(),

  city: nullableString().required(),

  state: nullableString().required(),

  zip: nullableString()
    .matches(/^\d{5}(?:[-\s]\d{4})?$/, "Incorrect zipcode format")
    .required(),

  invoicedCustomer: y.object().shape({
    __typename: y.string().strip(true),
  }),
});

const OrganizationForm:React.FC<Props> = (props: Props) => (
  <AdminFormLayout>
    <Formik
      initialValues={props.initialData}
      onSubmit={props.handleSubmit}
      validationSchema={formSchema}
      render={({ isSubmitting, values }) => (
        <Form>
          <Grid container spacing={2} className="card" sx={{ mb: "30px" }}>
            <Typography>Required fields marked with *</Typography>

            <Grid size={{ xs: 12 }}>
              <Grid size={{ xs: 12, sm: 8, md: 6 }}>
                <Input margin="none" label="Organization Name *" name="name" type="text" />
              </Grid>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Grid size={{ xs: 12, sm: 8, md: 6 }}>
                <Input margin="none" label="Main Contact Phone *" name="phone" type="text" />
              </Grid>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Grid size={{ xs: 12, sm: 8, md: 6 }}>
                <Input margin="none" label="Main Contact Email *" name="email" type="text" />
              </Grid>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Grid size={{ xs: 12, sm: 8, md: 6 }}>
                <Input margin="none" label="BCC Email" name="bccEmail" type="text" />
              </Grid>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Grid size={{ xs: 12, sm: 8, md: 6 }}>
                <Input margin="none" label="Address *" name="address1" type="text" />
              </Grid>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Grid size={{ xs: 12, sm: 8, md: 6 }}>
                <Input margin="none" label="Address 2" name="address2" type="text" />
              </Grid>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Grid size={{ xs: 12, sm: 8, md: 6 }}>
                <Input margin="none" label="City *" name="city" type="text" />
              </Grid>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Grid size={{ xs: 12, sm: 8, md: 6 }}>

                <MaterialFormikSelectAutocomplete
                  id="state"
                  margin="none"
                  name="state"
                  placeholder="State *"
                  title="State *"
                  options={extendedStatesList}
                />
              </Grid>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Grid size={{ xs: 12, sm: 8, md: 6 }}>
                <Input margin="none" label="Zip *" name="zip" type="text" />
              </Grid>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Grid size={{ xs: 12, sm: 8, md: 6 }}>
                <Checkbox
                  label="Enable Invoiced Billing"
                  name="invoicedCustomer.active"
                  checked={!!values.invoicedCustomer?.active}
                />
              </Grid>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Grid size={{ xs: 12, sm: 8, md: 6 }}>
                <Input margin="none" label="Invoiced Customer Number" name="invoicedCustomer.invoicedId" type="text" />
              </Grid>
            </Grid>
          </Grid>

          <Grid size={{ xs: 12 }} sx={{ mt: 2 }}>
            <HistoryBackButton marginRight size="L" />

            <SubmitButton isWide text="Submit" loadingText="Sending" isLoading={isSubmitting} />
          </Grid>
        </Form>
      )}
    />
  </AdminFormLayout>
);

export default OrganizationForm;
